<template>
  <v-dialog v-model="show" persistent max-width="460">
    <v-card class="onc-card">
      <div class="onc-header" :style="{ background: headerBg }">
        <v-icon color="white" class="mr-2">{{ headerIcon }}</v-icon>
        <span class="onc-header-title">{{ title }}</span>
      </div>

      <div class="onc-body">
        <template v-if="step === 1">
          <p class="onc-desc">{{ description }} Onaylamak için sipariş numarasını kopyalayıp aşağıdaki kutuya yapıştırın.</p>

          <div class="onc-field-label">SİPARİŞ NO</div>
          <div class="onc-copybox" @click="copyNumber">
            <span class="onc-copybox-num">{{ orderNumber }}</span>
            <span class="onc-copybox-action" :class="{ 'onc-copied': copied }">
              <v-icon small :color="copied ? 'green darken-1' : 'grey darken-1'">{{ copied ? 'check' : 'content_copy' }}</v-icon>
              <span class="onc-copybox-action-text">{{ copied ? 'Kopyalandı' : 'Kopyala' }}</span>
            </span>
          </div>

          <v-text-field
            v-model="typed"
            placeholder="Sipariş numarasını buraya yapıştırın"
            outlined
            dense
            hide-details
            class="onc-input mt-4"
            :color="headerColor"
          ></v-text-field>
        </template>

        <template v-else>
          <div class="onc-confirm2">
            <div class="onc-confirm2-icon" :style="{ background: headerBgSoft }">
              <v-icon size="34" :color="headerColor">{{ headerIcon }}</v-icon>
            </div>
            <p class="onc-confirm2-title">Emin misiniz?</p>
            <p class="onc-confirm2-text"><strong>{{ orderNumber }}</strong> {{ confirmQuestion }}</p>
          </div>
        </template>
      </div>

      <div class="onc-actions">
        <v-btn text @click="close">Vazgeç</v-btn>
        <v-btn
          v-if="step === 1"
          :color="headerColor"
          dark
          depressed
          :disabled="!typed || typed.trim() !== String(orderNumber).trim()"
          @click="step = 2"
        >Devam</v-btn>
        <v-btn
          v-else
          :color="headerColor"
          dark
          depressed
          :loading="loading"
          @click="$emit('confirm')"
        >{{ confirmText }}</v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
const COLOR_MAP = {
  error: "#dc2626",
  "deep-orange": "#ea580c",
  primary: "#2563eb",
  success: "#059669"
};

export default {
  name: "OrderNumberConfirmDialog",
  props: {
    value: { type: Boolean, default: false },
    orderNumber: { type: [String, Number], default: "" },
    loading: { type: Boolean, default: false },
    title: { type: String, default: "Onay" },
    description: { type: String, default: "Bu işlemi yapmak üzeresiniz." },
    confirmQuestion: { type: String, default: "numaralı sipariş için işlem yapılacak. Bu işlem geri alınamaz." },
    confirmText: { type: String, default: "Onayla" },
    headerColor: { type: String, default: "error" },
    headerIcon: { type: String, default: "help_outline" }
  },
  data() {
    return { step: 1, typed: "", copied: false };
  },
  computed: {
    show: {
      get() { return this.value; },
      set(v) { this.$emit("input", v); }
    },
    headerBg() {
      return COLOR_MAP[this.headerColor] || COLOR_MAP.error;
    },
    headerBgSoft() {
      const c = COLOR_MAP[this.headerColor] || COLOR_MAP.error;
      return c + "1a";
    }
  },
  watch: {
    value(v) {
      if (v) { this.step = 1; this.typed = ""; this.copied = false; }
    }
  },
  methods: {
    close() { this.$emit("input", false); },
    async copyNumber() {
      const text = String(this.orderNumber);
      try {
        await navigator.clipboard.writeText(text);
      } catch (e) {
        const el = document.createElement("textarea");
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 1500);
    }
  }
};
</script>

<style scoped>
.onc-card {
  border-radius: 16px !important;
  overflow: hidden;
}
.onc-header {
  display: flex;
  align-items: center;
  padding: 16px 22px;
}
.onc-header-title {
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 0.2px;
}
.onc-body {
  padding: 22px;
}
.onc-desc {
  font-size: 14px;
  color: #475569;
  line-height: 1.55;
  margin-bottom: 18px;
}
.onc-field-label {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}
.onc-copybox {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.onc-copybox:hover {
  border-color: #cbd5e1;
  background: #f1f5f9;
}
.onc-copybox-num {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.5px;
  overflow-wrap: anywhere;
}
.onc-copybox-action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  color: #64748b;
}
.onc-copybox-action-text {
  font-size: 12px;
  font-weight: 600;
}
.onc-copied .onc-copybox-action-text {
  color: #16a34a;
}
.onc-confirm2 {
  text-align: center;
  padding: 6px 0 4px;
}
.onc-confirm2-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}
.onc-confirm2-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 8px;
}
.onc-confirm2-text {
  font-size: 14px;
  color: #475569;
  line-height: 1.55;
  margin: 0;
}
.onc-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 18px 18px;
}
</style>
